/**
 * CateringQuoteFlow - Quote-first booking for catering services
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Calendar } from '../ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { 
  Calendar as CalendarIcon, Users, Utensils, Check, ChevronRight, Loader2,
  MapPin, Truck, AlertCircle, Upload
} from 'lucide-react';
import { format, addDays } from 'date-fns';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SERVICE_TYPES = [
  { value: 'buffet', label: 'Buffet Style', description: 'Self-service stations' },
  { value: 'plated', label: 'Plated Service', description: 'Individual plates served' },
  { value: 'live_cooking', label: 'Live Cooking', description: 'Chefs cook on-site' },
  { value: 'cocktail', label: 'Cocktail/Canapés', description: 'Finger food & drinks' }
];

const BUDGET_OPTIONS = [
  { value: '500-1000', label: 'Rs. 500-1000/head', tier: 'Budget' },
  { value: '1000-1500', label: 'Rs. 1000-1500/head', tier: 'Standard' },
  { value: '1500-2500', label: 'Rs. 1500-2500/head', tier: 'Premium' },
  { value: '2500+', label: 'Rs. 2500+/head', tier: 'Luxury' }
];

const DIETARY_OPTIONS = [
  { id: 'halal', label: 'Halal' },
  { id: 'vegetarian', label: 'Vegetarian' },
  { id: 'vegan', label: 'Vegan' },
  { id: 'no_pork', label: 'No Pork' },
  { id: 'no_beef', label: 'No Beef' },
  { id: 'gluten_free', label: 'Gluten Free' },
  { id: 'nut_free', label: 'Nut Free' },
  { id: 'dairy_free', label: 'Dairy Free' }
];

const EVENT_TYPES = [
  { value: 'weddings', label: 'Wedding' },
  { value: 'birthday', label: 'Birthday Party' },
  { value: 'corporate', label: 'Corporate Event' },
  { value: 'religious', label: 'Religious/Cultural' },
  { value: 'other', label: 'Other' }
];

export default function CateringQuoteFlow({ open, onClose, provider, onQuoteSubmitted }) {
  const [step, setStep] = useState(1);
  const [serviceType, setServiceType] = useState('');
  const [guestCount, setGuestCount] = useState('');
  const [budgetPerHead, setBudgetPerHead] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [eventTime, setEventTime] = useState('');
  const [eventType, setEventType] = useState('');
  const [dietaryRequirements, setDietaryRequirements] = useState([]);
  const [serviceStaffNeeded, setServiceStaffNeeded] = useState(false);
  const [deliveryOnly, setDeliveryOnly] = useState(false);
  const [venueAddress, setVenueAddress] = useState('');
  const [menuInspirations, setMenuInspirations] = useState('');
  const [notes, setNotes] = useState('');
  const [contactInfo, setContactInfo] = useState({ name: '', phone: '', email: '' });
  const [loading, setLoading] = useState(false);

  const cateringDetails = provider?.catering_details || {};
  const pricePerHead = provider?.price_per_head || [];
  const currency = provider?.currency || 'Rs';

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const quoteData = {
        provider_id: provider.id,
        event_date: format(selectedDate, 'yyyy-MM-dd'),
        event_type: eventType,
        guest_count: parseInt(guestCount),
        service_type: serviceType,
        budget_per_head: budgetPerHead,
        dietary_requirements: dietaryRequirements,
        service_staff_needed: serviceStaffNeeded,
        delivery_only: deliveryOnly,
        venue_address: venueAddress,
        notes: `${menuInspirations ? 'Menu ideas: ' + menuInspirations + '\n\n' : ''}${notes}`,
        contact: contactInfo
      };

      const response = await fetch(`${API_URL}/api/events/quotes/catering`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(quoteData)
      });

      if (response.ok) {
        toast.success('Quote request sent! The caterer will respond within 24 hours.');
        onQuoteSubmitted?.();
        onClose();
      } else {
        throw new Error('Quote request failed');
      }
    } catch (error) {
      toast.error('Failed to send quote request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Event Basics</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Service Type</label>
                <Select value={serviceType} onValueChange={setServiceType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select style" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICE_TYPES.filter(st => 
                      !cateringDetails.service_types || 
                      cateringDetails.service_types.includes(st.value)
                    ).map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        <div>
                          <p className="font-medium">{type.label}</p>
                          <p className="text-xs text-gray-500">{type.description}</p>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700">Number of Guests</label>
                <Input
                  type="number"
                  value={guestCount}
                  onChange={(e) => setGuestCount(e.target.value)}
                  placeholder="Expected guests"
                  min={cateringDetails.min_guests || 10}
                  max={cateringDetails.max_guests || 1000}
                />
                {cateringDetails.min_guests && (
                  <p className="text-xs text-gray-500 mt-1">
                    Min: {cateringDetails.min_guests} guests
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Budget Per Head</label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {BUDGET_OPTIONS.map(option => (
                  <button
                    key={option.value}
                    onClick={() => setBudgetPerHead(option.value)}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      budgetPerHead === option.value
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 hover:border-orange-300'
                    }`}
                  >
                    <p className="font-medium">{option.label}</p>
                    <p className="text-xs text-gray-500">{option.tier}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Menu Tiers Preview */}
            {pricePerHead.length > 0 && (
              <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                <p className="text-sm font-medium text-orange-800 mb-2">Available Menus:</p>
                <div className="space-y-1">
                  {pricePerHead.map(tier => (
                    <div key={tier.id} className="flex justify-between text-sm">
                      <span className="text-orange-700">{tier.name}</span>
                      <span className="font-medium">{currency} {tier.price}/head</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Dietary Requirements</h3>
            
            <div className="grid grid-cols-2 gap-2">
              {DIETARY_OPTIONS.map(option => (
                <label key={option.id} className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <Checkbox
                    checked={dietaryRequirements.includes(option.id)}
                    onCheckedChange={(checked) => {
                      setDietaryRequirements(prev => 
                        checked 
                          ? [...prev, option.id]
                          : prev.filter(id => id !== option.id)
                      );
                    }}
                  />
                  <span className="text-sm">{option.label}</span>
                </label>
              ))}
            </div>

            {/* Caterer's dietary options */}
            {cateringDetails.dietary_options && (
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-800">
                  <Check className="w-4 h-4 inline mr-2" />
                  This caterer offers: {cateringDetails.dietary_options.join(', ')}
                </p>
              </div>
            )}

            <div className="pt-4 border-t">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Service Options</h4>
              
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 mb-2">
                <Checkbox
                  checked={serviceStaffNeeded}
                  onCheckedChange={setServiceStaffNeeded}
                />
                <div>
                  <p className="font-medium">Service Staff</p>
                  <p className="text-xs text-gray-500">Include servers for plating & service</p>
                </div>
              </label>
              
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <Checkbox
                  checked={deliveryOnly}
                  onCheckedChange={setDeliveryOnly}
                />
                <div>
                  <p className="font-medium">Delivery Only</p>
                  <p className="text-xs text-gray-500">Food delivered, no on-site service</p>
                </div>
              </label>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Event Details</h3>
            
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700 mb-2 block">Event Date</label>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => date < new Date() || date > addDays(new Date(), 365)}
                  className="rounded-md border"
                />
              </div>
              
              <div className="flex-1 space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Service Time</label>
                  <Input
                    value={eventTime}
                    onChange={(e) => setEventTime(e.target.value)}
                    placeholder="e.g., 6:00 PM"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Event Type</label>
                  <Select value={eventType} onValueChange={setEventType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select event type" />
                    </SelectTrigger>
                    <SelectContent>
                      {EVENT_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Venue Address</label>
                  <Input
                    value={venueAddress}
                    onChange={(e) => setVenueAddress(e.target.value)}
                    placeholder="Delivery/service location"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Additional Details</h3>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Menu Ideas / Inspiration (Optional)</label>
              <Textarea
                value={menuInspirations}
                onChange={(e) => setMenuInspirations(e.target.value)}
                placeholder="Any specific dishes, cuisine styles, or menu ideas you'd like..."
                rows={3}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Special Requirements</label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any other requirements, timing needs, setup instructions..."
                rows={3}
              />
            </div>

            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5" />
                The caterer will review your requirements and send a detailed quote with menu options.
              </p>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Contact & Submit</h3>
            
            <div className="space-y-3">
              <Input
                placeholder="Your full name *"
                value={contactInfo.name}
                onChange={(e) => setContactInfo(c => ({ ...c, name: e.target.value }))}
              />
              <Input
                placeholder="Phone number *"
                value={contactInfo.phone}
                onChange={(e) => setContactInfo(c => ({ ...c, phone: e.target.value }))}
              />
              <Input
                type="email"
                placeholder="Email address *"
                value={contactInfo.email}
                onChange={(e) => setContactInfo(c => ({ ...c, email: e.target.value }))}
              />
            </div>

            {/* Summary */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
              <h4 className="font-medium">Quote Request Summary</h4>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-600">Service</span>
                  <span className="capitalize">{serviceType?.replace('_', ' ')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Guests</span>
                  <span>{guestCount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Budget</span>
                  <span>{budgetPerHead?.replace('-', ' - ')}/head</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Date</span>
                  <span>{selectedDate && format(selectedDate, 'MMM d, yyyy')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Event Type</span>
                  <span className="capitalize">{eventType}</span>
                </div>
                {dietaryRequirements.length > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Dietary</span>
                    <span className="text-right max-w-[150px] truncate capitalize">
                      {dietaryRequirements.join(', ')}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Service Staff</span>
                  <span>{serviceStaffNeeded ? 'Yes' : 'No'}</span>
                </div>
              </div>
              <div className="border-t pt-2 mt-2">
                <p className="text-xs text-gray-500">
                  Estimated range: {currency} {(parseInt(guestCount) * 1000).toLocaleString()} - {currency} {(parseInt(guestCount) * 2500).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        );
    }
  };

  if (!provider) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Utensils className="w-5 h-5 text-orange-600" />
            Request Catering Quote
          </DialogTitle>
          <DialogDescription>{provider.provider_name}</DialogDescription>
        </DialogHeader>

        {/* Progress */}
        <div className="flex items-center gap-2 mb-4">
          {[1, 2, 3, 4, 5].map((s) => (
            <React.Fragment key={s}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                s < step ? 'bg-orange-600 text-white' :
                s === step ? 'bg-orange-100 text-orange-700 border-2 border-orange-600' :
                'bg-gray-100 text-gray-400'
              }`}>
                {s < step ? <Check className="w-3 h-3" /> : s}
              </div>
              {s < 5 && <div className={`flex-1 h-1 ${s < step ? 'bg-orange-600' : 'bg-gray-200'}`} />}
            </React.Fragment>
          ))}
        </div>

        {renderStep()}

        <div className="flex justify-between pt-4 border-t">
          {step > 1 ? (
            <Button variant="outline" onClick={() => setStep(s => s - 1)}>Back</Button>
          ) : <div />}
          
          {step < 5 ? (
            <Button 
              onClick={() => setStep(s => s + 1)}
              disabled={
                (step === 1 && (!serviceType || !guestCount || !budgetPerHead)) ||
                (step === 3 && (!selectedDate || !eventType || !venueAddress))
              }
              className="bg-orange-600 hover:bg-orange-700"
            >
              Continue
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit}
              disabled={loading || !contactInfo.name || !contactInfo.phone || !contactInfo.email}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Send Quote Request
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
